import { Box } from '@mui/material'
import type { DateTime } from 'luxon'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type { ActivationGroup } from '@/features/activationGroup/types'
import type { FormattedRow } from '@/features/bessDashboard/types'
import BidLadderContent from '@/features/bidding/components/cascading/bid-ladder/BidLadderContent'
import BidLadderContentSymmetrical from '@/features/bidding/components/cascading/bid-ladder/BidLadderContentSymmetrical'
import BidOfferCascadingLeftSidebar from '@/features/bidding/components/cascading/BidOfferCascadingLeftSidebar'
import ComingSoon from '@/features/bidding/components/cascading/ComingSoon'
import MarketFeatureGuard from '@/features/bidding/components/cascading/MarketFeatureGuard'
import ReviewBidsContent from '@/features/bidding/components/cascading/review/ReviewBidsContent'
import ReviewBidsContentSymmetrical from '@/features/bidding/components/cascading/review/ReviewBidsContentSymmetrical'
import ReviewBidsContentVolume from '@/features/bidding/components/cascading/review/ReviewBidsContentVolume'
import type { UseBidLadderStateReturn } from '@/features/bidding/hooks/useBidLadderState'
import { useBidLadderState } from '@/features/bidding/hooks/useBidLadderState'
import type { BidOfferCascadingData } from '@/features/bidding/hooks/useBidOfferCascadingData'
import { useExcludedPtuGroups } from '@/features/bidding/hooks/useExcludedPtuGroups'
import type { UsePricingStrategyStateReturn } from '@/features/bidding/hooks/usePricingStrategyState'
import { usePricingStrategyState } from '@/features/bidding/hooks/usePricingStrategyState'
import type { AggregateAvailabilityResponse } from '@/features/bidding/types/aggregateAvailability'
import type { AggregateSuggestedBidResponse } from '@/features/bidding/types/aggregateSuggestedBid'
import type { DragPayload } from '@/features/bidding/types/bidLadderInput'
import type { DragPayloadSymmetrical } from '@/features/bidding/types/bidLadderInputSymmetrical'
import type { CascadeMarketId } from '@/features/bidding/types/market'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import { cascadeMarketToForecastMarket, isSymmetricMarket } from '@/features/bidding/utils/forecast/marketProgramNames'
import { hasActivationGroupsConcept, hasDragDropEnabled } from '@/features/bidding/utils/marketCapabilities'
import { wattHoursToMegawattHours } from '@/utils/powerEnergyTransformations'

// Left sidebar tab indices
const LEFT_SIDEBAR_TAB_GROUPS = 0
const LEFT_SIDEBAR_TAB_CONFIGURATION = 1

/**
 * Helper to check if a feature is enabled for the given market.
 * Currently mFRR, FCR-D, and FCR-N are fully implemented.
 */
function isFeatureEnabledForMarket(market: CascadeMarketId): boolean {
  const enabledMarkets: CascadeMarketId[] = ['mfrr', 'fcr-d', 'fcr-n']
  return enabledMarkets.includes(market)
}

type BidOfferCascadingContentProps = Readonly<{
  cascadingData: BidOfferCascadingData
  deliveryDay: MarketDate
  selectedDeliveryDay: DateTime
  selectedMarket: CascadeMarketId
  onBidCreationSuccess: () => void
}>

/**
 * This component is designed to be remounted when selectedMarket or selectedDeliveryDay changes,
 * which automatically resets all internal state (tabs, bid ladder state, pricing strategy, etc.).
 */
function BidOfferCascadingContent({
  cascadingData,
  deliveryDay,
  selectedDeliveryDay,
  selectedMarket,
  onBidCreationSuccess,
}: BidOfferCascadingContentProps) {
  // Determine if this is a symmetrical market (FCR-N)
  const isSymmetrical = isSymmetricMarket(cascadeMarketToForecastMarket(selectedMarket))
  // Determine if this market uses the activation groups concept (only mFRR does)
  const marketHasActivationGroups = hasActivationGroupsConcept(selectedMarket)

  // Destructure cascading data - guaranteed to be defined since parent handles loading
  const {
    capacityByActivationGroup,
    activationGroupsMap,
    regulationObjectGroups,
    forecastsData,
    aggregateSuggestedBids,
    aggregateAvailability,
  } = cascadingData

  // View state
  const [isReviewingBids, setIsReviewingBids] = useState(false)
  const [leftSidebarTab, setLeftSidebarTab] = useState(LEFT_SIDEBAR_TAB_GROUPS)
  const [isLeftSidebarCollapsed, setIsLeftSidebarCollapsed] = useState(false)

  // Track when dragging from ladder to show drop zone in sidebar
  const [ladderDragPayload, setLadderDragPayload] = useState<DragPayload | DragPayloadSymmetrical | null>(null)

  const handleLadderDragStart = useCallback((payload: DragPayload | DragPayloadSymmetrical) => {
    setLadderDragPayload(payload)
  }, [])

  const handleLadderDragEnd = useCallback(() => {
    setLadderDragPayload(null)
  }, [])

  // Unified bid ladder state - handles both directional and symmetrical markets
  // FCR markets (without activation groups) need volume capacity config initialized
  const bidLadder = useBidLadderState({
    isSymmetrical,
    selectedMarket,
    withVolumeCapacity: !marketHasActivationGroups,
  })

  // Get initial level IDs for pricing strategy
  const initialLevelIds = useMemo(() => bidLadder.state.levels.map((l) => l.id), [bidLadder.state.levels])

  // Pricing strategy configuration for forecast-based pricing
  const pricingStrategy = usePricingStrategyState(initialLevelIds)

  // PTU exclusion state for mFRR per-group toggling
  const { excludedPtuGroups, togglePtuGroup, removeStaleExclusions } = useExcludedPtuGroups()

  // Clean up PTU exclusions for groups that are no longer assigned
  const { assignedGroupUuids } = bidLadder
  useEffect(() => {
    removeStaleExclusions(assignedGroupUuids)
  }, [assignedGroupUuids, removeStaleExclusions])

  // Extract syncWithLevels to avoid dependency on entire pricingStrategy object
  const { syncWithLevels } = pricingStrategy

  // Sync pricing config when levels change
  useEffect(() => {
    const levelIds = bidLadder.state.levels.map((l) => l.id)
    syncWithLevels(levelIds)
  }, [bidLadder.state.levels, syncWithLevels])

  // Open pricing configuration in sidebar (called when clicking forecast chip)
  const handleOpenPricingConfiguration = useCallback(() => {
    setLeftSidebarTab(LEFT_SIDEBAR_TAB_CONFIGURATION)
    setIsLeftSidebarCollapsed(false)
  }, [])

  // Go back to bid ladder view
  const handleBackToBidLadder = useCallback(() => {
    setIsReviewingBids(false)
  }, [])

  // Navigate to review bids view
  const handleReviewBids = useCallback(() => {
    setIsReviewingBids(true)
  }, [])

  // Calculate total available MW from all regulation object groups
  const totalCapacityWh = regulationObjectGroups.reduce((sum, group) => sum + group.totalCapacity, 0)
  const totalMWAvailable = wattHoursToMegawattHours(totalCapacityWh)

  // Determine which features are enabled for the current market
  const isMarketEnabled = isFeatureEnabledForMarket(selectedMarket)

  // Determine if drag and drop is enabled for the current market
  const isDragDropEnabled = hasDragDropEnabled(selectedMarket)

  // For the sidebar, convert symmetrical payload to directional by adding direction to previewGroups
  const sidebarDragPayload = useMemo((): DragPayload | null => {
    if (!ladderDragPayload) return null

    if (isSymmetrical) {
      return {
        regulationObjectCodes: ladderDragPayload.regulationObjectCodes,
        activationGroups: ladderDragPayload.activationGroups,
        sourceLevelId: ladderDragPayload.sourceLevelId,
        previewGroups: ladderDragPayload.previewGroups?.map((g) => ({
          ...g,
          direction: 'up' as const,
        })),
      }
    }

    return ladderDragPayload as DragPayload
  }, [isSymmetrical, ladderDragPayload])

  return (
    <>
      {marketHasActivationGroups && (
        <BidOfferCascadingLeftSidebar
          activeTab={leftSidebarTab}
          assignGroups={bidLadder.assignGroups}
          assignedGroupUuids={bidLadder.assignedGroupUuids}
          bidLevels={
            bidLadder.isSymmetrical
              ? bidLadder.state.levels.map((l) => ({
                  id: l.id,
                  upPrice: l.price,
                  downPrice: l.price,
                  assignedGroups: l.assignedGroups.map((g) => ({ ...g, direction: 'up' as const })),
                }))
              : bidLadder.state.levels
          }
          capacityByActivationGroup={capacityByActivationGroup}
          forecastsData={forecastsData}
          isCollapsed={isLeftSidebarCollapsed}
          ladderDragPayload={sidebarDragPayload}
          pricingStrategy={pricingStrategy}
          regulationObjectGroups={regulationObjectGroups}
          selectedDeliveryDay={selectedDeliveryDay}
          selectedMarket={selectedMarket}
          unassignRegulationObjectGroup={bidLadder.unassignRegulationObjectGroup}
          onCollapsedChange={setIsLeftSidebarCollapsed}
          onLadderDropComplete={handleLadderDragEnd}
          onTabChange={setLeftSidebarTab}
        />
      )}

      {/* Right Content - fills remaining space */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        {isMarketEnabled ? (
          <MainContent
            activationGroupsMap={activationGroupsMap}
            aggregateAvailability={aggregateAvailability}
            aggregateSuggestedBids={aggregateSuggestedBids}
            bidLadder={bidLadder}
            deliveryDay={deliveryDay}
            excludedPtuGroups={excludedPtuGroups}
            forecastsData={forecastsData}
            isDragDropEnabled={isDragDropEnabled}
            isReviewingBids={isReviewingBids}
            marketHasActivationGroups={marketHasActivationGroups}
            pricingStrategy={pricingStrategy}
            selectedDeliveryDay={selectedDeliveryDay}
            selectedMarket={selectedMarket}
            totalMWAvailable={totalMWAvailable}
            onBackToBidLadder={handleBackToBidLadder}
            onBidCreationSuccess={onBidCreationSuccess}
            onDragEndFromLadder={handleLadderDragEnd}
            onDragStartFromLadder={handleLadderDragStart}
            onOpenPricingConfiguration={handleOpenPricingConfiguration}
            onReviewBids={handleReviewBids}
            onTogglePtuGroup={togglePtuGroup}
          />
        ) : (
          <ComingSoon />
        )}
      </Box>
    </>
  )
}

type MainContentProps = Readonly<{
  activationGroupsMap: Map<string, ActivationGroup>
  bidLadder: UseBidLadderStateReturn
  aggregateAvailability: AggregateAvailabilityResponse | null
  aggregateSuggestedBids: AggregateSuggestedBidResponse | null
  deliveryDay: MarketDate
  excludedPtuGroups: Set<string>
  forecastsData: FormattedRow[] | undefined
  isDragDropEnabled: boolean
  isReviewingBids: boolean
  marketHasActivationGroups: boolean
  pricingStrategy: UsePricingStrategyStateReturn
  selectedDeliveryDay: DateTime
  selectedMarket: CascadeMarketId
  totalMWAvailable: number
  onBackToBidLadder: () => void
  onBidCreationSuccess: () => void
  onDragEndFromLadder: () => void
  onDragStartFromLadder: (payload: DragPayload | DragPayloadSymmetrical) => void
  onOpenPricingConfiguration: () => void
  onReviewBids: () => void
  onTogglePtuGroup: (groupUuid: string, hour: number) => void
}>

function MainContent({
  activationGroupsMap,
  bidLadder,
  aggregateAvailability,
  aggregateSuggestedBids,
  deliveryDay,
  excludedPtuGroups,
  forecastsData,
  isDragDropEnabled,
  isReviewingBids,
  marketHasActivationGroups,
  pricingStrategy,
  selectedDeliveryDay,
  selectedMarket,
  totalMWAvailable,
  onBackToBidLadder,
  onBidCreationSuccess,
  onDragEndFromLadder,
  onDragStartFromLadder,
  onOpenPricingConfiguration,
  onReviewBids,
  onTogglePtuGroup,
}: MainContentProps) {
  if (bidLadder.isSymmetrical) {
    if (!marketHasActivationGroups && isReviewingBids) {
      return (
        <MarketFeatureGuard selectedMarket={selectedMarket}>
          <ReviewBidsContentVolume
            isSymmetrical
            aggregateAvailability={aggregateAvailability}
            aggregateSuggestedBids={aggregateSuggestedBids}
            deliveryDay={deliveryDay}
            levels={bidLadder.state.levels}
            selectedMarket={selectedMarket}
            onBackClick={onBackToBidLadder}
            onSuccess={onBidCreationSuccess}
          />
        </MarketFeatureGuard>
      )
    }

    if (isReviewingBids) {
      return (
        <MarketFeatureGuard selectedMarket={selectedMarket}>
          <ReviewBidsContentSymmetrical
            activationGroupsMap={activationGroupsMap}
            deliveryDay={deliveryDay}
            forecastsData={forecastsData}
            levels={bidLadder.state.levels}
            pricingConfig={pricingStrategy.config}
            selectedDeliveryDay={selectedDeliveryDay}
            selectedMarket={selectedMarket}
            onBackClick={onBackToBidLadder}
            onSuccess={onBidCreationSuccess}
          />
        </MarketFeatureGuard>
      )
    }

    return (
      <MarketFeatureGuard selectedMarket={selectedMarket}>
        <BidLadderContentSymmetrical
          activationGroupsCount={activationGroupsMap.size}
          aggregateAvailability={aggregateAvailability}
          aggregateSuggestedBids={aggregateSuggestedBids}
          bidLadderState={bidLadder.state}
          deliveryDay={deliveryDay}
          forecastsData={forecastsData}
          isDragDropEnabled={isDragDropEnabled}
          pricingStrategy={pricingStrategy}
          selectedDeliveryDay={selectedDeliveryDay}
          selectedMarket={selectedMarket}
          totalMWAvailable={totalMWAvailable}
          onDragEndFromLadder={isDragDropEnabled ? onDragEndFromLadder : undefined}
          onDragStartFromLadder={isDragDropEnabled ? onDragStartFromLadder : undefined}
          onOpenPricingConfiguration={marketHasActivationGroups ? onOpenPricingConfiguration : undefined}
          onReviewBids={onReviewBids}
        />
      </MarketFeatureGuard>
    )
  }

  if (!marketHasActivationGroups && isReviewingBids) {
    return (
      <MarketFeatureGuard selectedMarket={selectedMarket}>
        <ReviewBidsContentVolume
          aggregateAvailability={aggregateAvailability}
          aggregateSuggestedBids={aggregateSuggestedBids}
          deliveryDay={deliveryDay}
          isSymmetrical={false}
          levels={bidLadder.state.levels}
          selectedMarket={selectedMarket}
          onBackClick={onBackToBidLadder}
          onSuccess={onBidCreationSuccess}
        />
      </MarketFeatureGuard>
    )
  }

  if (isReviewingBids) {
    return (
      <MarketFeatureGuard selectedMarket={selectedMarket}>
        <ReviewBidsContent
          activationGroupsMap={activationGroupsMap}
          deliveryDay={deliveryDay}
          excludedPtuGroups={excludedPtuGroups}
          forecastsData={forecastsData}
          levels={bidLadder.state.levels}
          pricingConfig={pricingStrategy.config}
          selectedDeliveryDay={selectedDeliveryDay}
          selectedMarket={selectedMarket}
          onBackClick={onBackToBidLadder}
          onSuccess={onBidCreationSuccess}
        />
      </MarketFeatureGuard>
    )
  }

  return (
    <MarketFeatureGuard selectedMarket={selectedMarket}>
      <BidLadderContent
        activationGroupsCount={activationGroupsMap.size}
        aggregateAvailability={aggregateAvailability}
        aggregateSuggestedBids={aggregateSuggestedBids}
        bidLadderState={bidLadder.state}
        deliveryDay={deliveryDay}
        excludedPtuGroups={marketHasActivationGroups ? excludedPtuGroups : undefined}
        forecastsData={forecastsData}
        isDragDropEnabled={isDragDropEnabled}
        pricingStrategy={pricingStrategy}
        selectedDeliveryDay={selectedDeliveryDay}
        selectedMarket={selectedMarket}
        totalMWAvailable={totalMWAvailable}
        onDragEndFromLadder={isDragDropEnabled ? onDragEndFromLadder : undefined}
        onDragStartFromLadder={isDragDropEnabled ? onDragStartFromLadder : undefined}
        onOpenPricingConfiguration={marketHasActivationGroups ? onOpenPricingConfiguration : undefined}
        onReviewBids={onReviewBids}
        onTogglePtuGroup={marketHasActivationGroups ? onTogglePtuGroup : undefined}
      />
    </MarketFeatureGuard>
  )
}

export default BidOfferCascadingContent
